import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import {
  Alert,
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import { showUserDisplayName } from "../../../lib/userUtils";
import {
  checkCountryCompatibility,
  checkTechLayerCompatibility,
  formatPlaceholder,
} from "../../../lib/utils";
import { getConfigurations, getFieldConfigurations } from "../../../modules/config";
import { getProjectConfig } from "../../../modules/executions";
import { automationStatuses } from "../../../static-data/DropdownOptions";
import CustomConfigCreateForm from "../../configurations/config/CustomConfigCreateForm";
import SkeletonLoader from "../../elements/SkeletonLoader";
import { updateTestSuiteCountries } from "../../../modules/testsuites";
import { formatCountryOptions } from "../../../static-data/formatCountryOptions";
import CustomInputField from "../../../ui/primitives/CustomInputField";
import CustomSelect from "../../../ui/primitives/CustomSelect";
import MultiSelectDropdown from "../../../ui/primitives/MultiSelectDropdown";
import { useAccess } from "../../../hooks/useAccess";


const validateStep1 = (formData) => {
  const errors = {};
  if (!formData.summary?.trim()) errors.summary = "Description is required";
  return errors;
};

const validateStep2 = (formData, statuses = [], isReviewWorkflow = false) => {
  const errors = {};
  if (!formData.priority) errors.priority = "Priority is required";
  if (!formData.status) errors.status = "Status is required";
  if (!formData.owner) errors.owner = "Author is required";
  if (!formData.technologyLayer) errors.technologyLayer = "Technology Layer is required";
  if (!formData.testsuites?.length) errors.testsuites = "At least one Test Suite is required";

  const currentStatusName = statuses.find((s) => s.id === formData.status)?.name || "";
  if (isReviewWorkflow && currentStatusName === "In Review" && !formData.reviewer) {
    errors.reviewer = "Reviewer is required when status is In Review";
  }
  return errors;
};

const validateStep3 = (formData) => {
  const errors = {};
  if (formData.isAutomationNeeded === true && formData.isAutomated === "Yes" && !formData.automationMethod?.trim())
    errors.automationMethod = "Automation Method is required";
  const requiresCategoryFields =
    formData.isAutomationNeeded === true &&
    formData.isAutomated === "No" &&
    formData.automationStatus !== "To Be Planned";
  if (requiresCategoryFields && !formData.notAutomatedCategory)
    errors.notAutomatedCategory = "Category is required";
  if (requiresCategoryFields && !formData.notAutomatedSubCategory)
    errors.notAutomatedSubCategory = "Sub Category is required";
  return errors;
};

// ── Layout primitives ─────────────────────────────────────────────────────────

const StaticText = ({ children }) => (
  <Typography variant="body1" sx={{ color: "text.primary", py: 0.5, minHeight: 32, display: "flex", alignItems: "center" }}>
    {children}
  </Typography>
);

const FieldRow = ({ children, sx }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2.5, ...sx }}>
    {children}
  </Box>
);

const FieldGroup = ({ children, sx }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, ...sx }}>
    {children}
  </Box>
);

const FieldDisplay = ({ label, children }) => (
  <FieldGroup>
    <Typography variant="body2" fontWeight={600} color="text.primary" fontSize="0.875rem">
      {label}
    </Typography>
    <StaticText>{children}</StaticText>
  </FieldGroup>
);

const getUserId = (u) => u.user_id ?? u.id;

const usersToOptions = (users = []) =>
  users.map((u) => ({
    id: getUserId(u),
    name: u.full_name || u.email || u.username,
    disabled: u.is_active === false || u.status === "inactive",
    extra: u.is_active === false || u.status === "inactive" ? "(Inactive)" : undefined,
  }));

const toOptions = (arr = []) => arr.map((item) => ({ id: item.id, name: item.name }));

// ── Main component ────────────────────────────────────────────────────────────

const CreateTestCaseForm = ({
  formData,
  setFormData,
  isEditing,
  defaultFormData,
  statuses = [],
  testSuites = [],
  priorities = [],
  modules = [],
  labels = [],
  projectUsers = [],
  loading,
  setIsFormEdited = () => { },
  isReviewWorkflow,
  refetchData,
  technologyLayers,
  isCreateMode = false,
  onAddSteps = null,
  stepsCount = 0,
  createStep = 1,
  setCreateStep,
  validateAllAndSubmitRef = null,
  validateAllRef = null,
}) => {
  const { currentUser: authUser } = useUser();
  const { canAccess } = useAccess();
  const canManageTestCases = canAccess('ReviewTestCases');
  const currentUserId = authUser?.user_id ?? authUser?.id;

  const [selectedTestSuites, setTestSuites] = useState(formData?.testsuites || []);
  const [projectConfig, setProjectConfig] = useState({});
  const [selectedCountries, setSelectedCountries] = useState(formData?.country || []);

  const hasCountryConfig =
    projectConfig?.multiple_geographies === "Yes" && projectConfig?.geographies?.length > 0;

  const [selectedLabels, setSelectedLabel] = useState(formData?.labels || []);
  useEffect(() => { setSelectedLabel(formData?.labels || []); }, [formData?.labels]);

  const [techWarning, setTechWarning] = useState(null);
  const [countryWarnings, setCountryWarnings] = useState(null);
  const [labelOptions, setLabelOptions] = useState(labels);
  const [notAutomatedCategories, setNotAutomatedCategories] = useState([]);
  const [notAutomatedSubCategories, setNotAutomatedSubCategories] = useState([]);

  const [stepErrors, setStepErrors] = useState({});

  const currentStatus = statuses.find((s) => s.id === formData.status)?.name || "";
  const { projectId } = useParams();

  useEffect(() => {
    const techLayerName = technologyLayers?.find(t => t.id === formData.technologyLayer)?.name;
    setTechWarning(checkTechLayerCompatibility(selectedTestSuites, techLayerName, testSuites));
    setCountryWarnings(checkCountryCompatibility(selectedCountries, formData.testsuites, testSuites));
  }, [selectedTestSuites, selectedCountries, formData.technologyLayer]);

  const handleChangeSuite = (newTestSuites) => {
    setTestSuites(newTestSuites);
    setFormData((prev) => ({ ...prev, testsuites: newTestSuites }));
    setIsFormEdited(true);
    if (stepErrors.testsuites) setStepErrors((e) => ({ ...e, testsuites: undefined }));
  };

  const handleCountryChange = (value) => {
    setSelectedCountries(value);
    setFormData((prev) => ({ ...prev, country: value }));
    setIsFormEdited(true);
  };

  const handleChangeLabel = (newLabels) => {
    setSelectedLabel(newLabels);
    setFormData({ ...formData, labels: newLabels });
    setIsFormEdited(true);
  };

  const handleInputChange = useCallback((id, value) => {
    const newValue = value === "" ? null : value;
    let updates = { [id]: newValue };

    if (id === "technologyLayer") {
      const techLayerName = technologyLayers?.find(t => t.id === formData.technologyLayer)?.name;
      setTechWarning(checkTechLayerCompatibility(selectedTestSuites, techLayerName, testSuites));
      if (stepErrors.technologyLayer) setStepErrors((e) => ({ ...e, technologyLayer: undefined }));
    }

    if (id === "isAutomationNeeded") {
      updates = value === false
        ? { isAutomationNeeded: false, isAutomated: "No", automationStatus: null, automatedBy: null, automationMethod: "" }
        : { isAutomationNeeded: true, isAutomated: "No", automationStatus: formData.automationStatus || "Todo", reasonForNotAutomated: null, notAutomatedCategory: null, notAutomatedSubCategory: null };
    }

    if (id === "isAutomated") {
      updates = value === "Yes"
        ? { isAutomated: "Yes", automationStatus: "Done", automatedBy: currentUserId || (projectUsers.length > 0 ? projectUsers[0].id : null), isAutomationNeeded: true, reasonForNotAutomated: null, notAutomatedCategory: null, notAutomatedSubCategory: null }
        : { isAutomated: "No", automatedBy: null, automationMethod: "", automationStatus: formData.isAutomationNeeded === true ? "Todo" : null };
    }
    if (stepErrors[id]) setStepErrors((e) => ({ ...e, [id]: undefined }));
    setFormData((prev) => ({ ...prev, ...updates }));
    setIsFormEdited(true);
  }, [selectedTestSuites, setFormData, stepErrors, formData.automationStatus, formData.isAutomationNeeded]);

  const fetchTestCaseData = useCallback(() => {
    const updates = {};
    if (statuses && formData.status === "") {
      const def = statuses.find((s) => s.is_default);
      if (def) updates.status = def.id;
    }
    if (priorities && formData.priority === "") {
      const def = priorities.find((p) => p.is_default);
      if (def) updates.priority = def.id;
    }
    if (projectUsers?.length > 0 && (formData.owner === "" || formData.owner === null)) {
      updates.owner = currentUserId || projectUsers[0].id;
    }
    if (Object.keys(updates).length > 0) setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchDropdownData = async () => {
    try {
      const labelResponse = await getConfigurations("labels", projectId);
      const fieldResponse = await getFieldConfigurations(projectId);
      setLabelOptions(labelResponse?.data || []);
      const categories = (fieldResponse?.data || []).map((cat) => ({ ...cat, subCategories: cat.sub_categories || [] }));
      setNotAutomatedCategories(categories);
      if (formData.notAutomatedCategory) {
        const sel = categories.find((c) => c.id === formData.notAutomatedCategory);
        setNotAutomatedSubCategories(sel?.subCategories || []);
      }
    } catch { console.log("Failed to fetch data."); }
  };

  useEffect(() => {
    const fetchProjectData = async () => {
      try { setProjectConfig(await getProjectConfig(projectId)); }
      catch (error) { console.error("Error fetching project config:", error); }
    };
    fetchProjectData();
  }, [projectId]);

  const filterSelectedValues = (selectedValues, availableOptions) =>
    selectedValues?.filter((value) => availableOptions?.includes(value));

  useEffect(() => {
    fetchTestCaseData();
    fetchDropdownData();
    return () => { setFormData(defaultFormData); setTechWarning(null); setCountryWarnings(null); };
  }, []);

  const handleUpdateTestSuite = async () => {
    if (!countryWarnings?.length) return;
    try {
      await updateTestSuiteCountries({ updates: countryWarnings.map((w) => ({ suiteId: w.testSuiteId, geographies: w.countries })) });
      refetchData();
      setCountryWarnings(null);
    } catch (error) { console.error("Failed to update test suites:", error); }
  };

 const handleNotAutomatedChange = (type, value) => {
    const newValue = value === "" ? null : value;
    setFormData((prev) => {
      if (type === "category") {
        const category = notAutomatedCategories.find((c) => c.id === newValue);
        setNotAutomatedSubCategories(category?.subCategories || []);
        return { ...prev, notAutomatedCategory: newValue, notAutomatedSubCategory: null };
      }
      return { ...prev, notAutomatedSubCategory: newValue };
    });
    const errKey = type === "category" ? "notAutomatedCategory" : "notAutomatedSubCategory";
    if (stepErrors[errKey]) setStepErrors((e) => ({ ...e, [errKey]: undefined }));
    setIsFormEdited(true);
  };

  // ── Step navigation with validation ──────────────────────────────────────

  const goToNextStep = useCallback(() => {
    let errors = {};
    if (createStep === 1) errors = validateStep1(formData);
    if (createStep === 2) errors = validateStep2(formData, statuses, isReviewWorkflow);
    if (Object.keys(errors).length > 0) { setStepErrors(errors); return; }
    setStepErrors({});
    setCreateStep((s) => s + 1);
  }, [createStep, formData, setCreateStep, statuses]);

  const goToPrevStep = useCallback(() => {
    setStepErrors({});
    setCreateStep((s) => s - 1);
  }, [setCreateStep]);

  const validateAllAndSubmit = useCallback((onSubmit) => {
    const errors = validateStep3(formData);
    if (Object.keys(errors).length > 0) { setStepErrors(errors); return; }
    setStepErrors({});
    onSubmit?.();
  }, [formData]);

  const validateAll = useCallback(() => {
    const errors = {
      ...validateStep1(formData),
      ...validateStep2(formData, statuses, isReviewWorkflow),
      ...validateStep3(formData),
    };
    setStepErrors(errors);
    return errors;
  }, [formData, statuses, isReviewWorkflow]);

  useEffect(() => {
    if (validateAllAndSubmitRef) {
      validateAllAndSubmitRef.current = validateAllAndSubmit;
      validateAllAndSubmitRef._goToNextStep = goToNextStep;
      validateAllAndSubmitRef._goToPrevStep = goToPrevStep;
    }
  }, [validateAllAndSubmitRef, validateAllAndSubmit, goToNextStep, goToPrevStep]);

  useEffect(() => {
    if (validateAllRef) {
      validateAllRef.current = validateAll;
    }
  }, [validateAllRef, validateAll]);

  // ── Reviewer filtering ────────────────────────────────────────────────────

  const getReviewerOptions = () => {
    let options = projectUsers.filter((u) => getUserId(u) !== formData.owner);
    if (formData.module) {
      const moduleFiltered = options.filter(
        (u) => u.modules?.includes(formData.module) || u.module_ids?.includes(formData.module)
      );
      if (moduleFiltered.length > 0) options = moduleFiltered;
    }
    return options;
  };

  // ── Field group renderers ─────────────────────────────────────────────────

  const renderBasicFields = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {isEditing ? (
        <CustomInputField id="summary" label="Description" required value={formData.summary || ""} onChange={handleInputChange} placeholder="Enter description" maxLength={255} error={stepErrors.summary} />
      ) : (
        <FieldDisplay label="Description">
          {formData.summary || formatPlaceholder("Add a description")}
        </FieldDisplay>
      )}

      {isEditing ? (
        <CustomInputField id="objective" label="Objective" value={formData.objective || ""} onChange={handleInputChange} placeholder="Enter objective" maxLength={500} multiline rows={3} />
      ) : (
        <FieldDisplay label="Objective">
          {formData.objective || formatPlaceholder("Add an objective")}
        </FieldDisplay>
      )}

      {isEditing ? (
        <CustomInputField id="preCondition" label="Pre-condition" value={formData.preCondition || ""} onChange={handleInputChange} placeholder="Enter pre-condition" maxLength={500} multiline rows={3} />
      ) : (
        <FieldDisplay label="Pre-condition">
          {formData.preCondition || formatPlaceholder("Add a pre-condition")}
        </FieldDisplay>
      )}
    </Box>
  );

  const renderClassificationFields = () => {
    const isOwnTestCase = formData.owner === currentUserId;
    const isAssignedReviewer = !!formData.reviewer && formData.reviewer === currentUserId;
    const canApprove = canManageTestCases || (isReviewWorkflow && isAssignedReviewer && !isOwnTestCase);
    const approvalBlocked = !canApprove;

    const statusOptions = statuses.map((s) => ({
      id: s.id,
      name: s.name,
      disabled: s.name === "Approved" && approvalBlocked,
    }));

    const approvalHelperText = !approvalBlocked ? undefined : isReviewWorkflow && isOwnTestCase
      ? "You can't approve your own test case. Assign a reviewer to submit it for approval."
      : isReviewWorkflow ? "Only the assigned reviewer or an admin can approve this test case."
      : "You don't have permission to approve test cases.";

    const reviewerOpts = [
      { id: "__none__", name: "None" },
      ...usersToOptions(getReviewerOptions()),
    ];

    const testSuiteOptions = testSuites.map((s) => ({
      id: s.id,
      name: s.name,
      status: s.status,
      disabled: s.status === "suspended",
    }));

    const formatted = formatCountryOptions(projectConfig?.geographies || []);
    const displayMap = {};
    formatted.forEach((item) => { displayMap[item.value] = item.display; });
    const countryOpts = projectConfig?.geographies?.map((g) => ({ id: g, name: g }));

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Row 1: Priority / Status / Reviewer */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2.5 }}>
          {/* Priority */}
          {isEditing ? (
            <CustomSelect id="priority" label="Priority" required value={formData?.priority || ""} onChange={(val) => handleInputChange("priority", val)} options={toOptions(priorities)} placeholder="Select priority" error={stepErrors.priority} />
          ) : (
            <FieldDisplay label="Priority">
              {priorities?.find((p) => p.id === formData?.priority)?.name || "—"}
            </FieldDisplay>
          )}

          {/* Status */}
          {isEditing ? (
            <FieldGroup>
              <CustomSelect
                id="status"
                label="Status"
                required
                value={formData.status || ""}
                onChange={(val) => handleInputChange("status", val)}
                options={statusOptions}
                placeholder="Select status"
                error={stepErrors.status}
                helperText={approvalHelperText}
              />
              {currentStatus === "Archived" && (
                <Typography variant="caption" color="error.main">*Archiving test case will remove it from all test suites.</Typography>
              )}
            </FieldGroup>
          ) : (
            <FieldDisplay label="Status">
              {currentStatus || "—"}
              {currentStatus === "Approved" && (
                <Box component="span" sx={{ ml: 0.5, fontSize: "0.75rem", color: "text.secondary" }}>
                  (By: {projectUsers.find((u) => u.id === formData.approvedBy)?.full_name || ""})
                </Box>
              )}
            </FieldDisplay>
          )}

          {/* Reviewer (review workflow only) */}
          <Box>
          {isReviewWorkflow && (
            isEditing ? (
              <CustomSelect
                id="reviewer"
                label={
                  <>
                    Reviewer
                    {currentStatus === "In Review" && <Box component="span" sx={{ color: "error.main" }}> *</Box>}
                  </>
                }
                value={getReviewerOptions().some((u) => u.id === formData.reviewer) ? formData.reviewer : "__none__"}
                onChange={(val) => handleInputChange("reviewer", val === "__none__" ? null : val)}
                options={reviewerOpts}
                disabled={currentStatus !== "In Review"}
                error={stepErrors.reviewer}
                helperText={currentStatus !== "In Review" ? 'Available when status is "In Review"' : undefined}
              />
            ) : (
              <FieldDisplay label="Reviewer">
                {showUserDisplayName(formData?.reviewer, projectUsers) || "None"}
              </FieldDisplay>
            )
          )}
          </Box>
        </Box>

        {/* Row 3: Technology Layer / Test Suite / Countries */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2.5 }}>
          {/* Technology Layer */}
          {isEditing ? (
            <CustomSelect
              id="technologyLayer"
              label="Technology Layer"
              required
              value={formData.technologyLayer || ""}
              onChange={(val) => handleInputChange("technologyLayer", val || null)}
              options={[...toOptions(technologyLayers)]}
              error={stepErrors.technologyLayer}
              placeholder="Select technology layer"
            />
          ) : (
            <FieldDisplay label="Technology Layer">
              {technologyLayers?.find((s) => s.id === formData.technologyLayer)?.name || "None"}
            </FieldDisplay>
          )}
          {isEditing ? (
            <MultiSelectDropdown
              label="Test Suite"
              required
              options={testSuiteOptions}
              selectedValues={selectedTestSuites}
              onChange={handleChangeSuite}
              placeholder="Select test suites"
              error={stepErrors.testsuites}
              name="test suites"
              renderOption={(opt) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={{ textTransform: "none" }}>
                    {opt.name}
                  </Typography>
                  {opt.status === "suspended" && (
                    <Typography variant="caption" color="error.dark">
                      (Suspended)
                    </Typography>
                  )}
                </Box>
              )}
            />
          ) : (
            <FieldDisplay label="Test Suite">
              {formData.testsuites?.length > 0
                ? testSuites.filter((s) => formData.testsuites.indexOf(s.id) > -1).map((e) => e.name).join(", ")
                : "None"}
            </FieldDisplay>
          )}
          <Box sx={{ display: "grid"}}>
          {hasCountryConfig && (
            isEditing ? (
              <MultiSelectDropdown
                label="Countries"
                options={countryOpts}
                selectedValues={filterSelectedValues(selectedCountries, projectConfig.geographies)}
                onChange={handleCountryChange}
                placeholder="Select countries"
                name="countries"
                renderOption={(opt) => displayMap[opt.id] || opt.name}
              />
            ) : (
              <FieldDisplay label="Countries">
                {filterSelectedValues(formData?.country, projectConfig?.geographies)?.length > 0
                  ? filterSelectedValues(formData.country, projectConfig.geographies).join(", ")
                  : "None"}
              </FieldDisplay>
            )
          )}
          </Box>
        </Box>
        {/* Row 3: Labels / Author / Module */}
        <FieldRow>
          {isEditing ? (
            <FieldGroup>
              <MultiSelectDropdown
                label="Labels"
                options={toOptions(labelOptions)}
                selectedValues={selectedLabels}
                onChange={handleChangeLabel}
                placeholder="Select labels"
                name="labels"
                showSelectAll={false}
              />
              <CustomConfigCreateForm configType="labels" fetchDropdownData={fetchDropdownData} />
            </FieldGroup>
          ) : (
            <FieldDisplay label="Labels">
              {formData.labels?.length > 0
                ? labels.filter((l) => formData.labels.indexOf(l.id) > -1).map((e) => e.name).join(", ")
                : "None"}
            </FieldDisplay>
          )}

          {/* Author */}
          {isEditing ? (
            <CustomSelect
              id="owner"
              label="Author"
              required
              value={projectUsers?.some((u) => u.id === formData.owner) ? formData.owner : ""}
              onChange={(val) => handleInputChange("owner", val)}
              options={usersToOptions(projectUsers)}
              placeholder="Select author"
              error={stepErrors.owner}
            />
          ) : (
            <FieldDisplay label="Author">
              {showUserDisplayName(formData?.owner, projectUsers)}
            </FieldDisplay>
          )}

          {/* Module */}
          {isEditing ? (
            <CustomSelect
              id="module"
              label="Module"
              value={formData.module || ""}
              onChange={(val) => handleInputChange("module", val || null)}
              options={[{ id: "", name: "None" }, ...toOptions(modules)]}
            />
          ) : (
            <FieldDisplay label="Module">{modules?.find((m) => m.id === formData.module)?.name || "None"}</FieldDisplay>
          )}
        </FieldRow>

        {/* Warnings */}
        {techWarning && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight={600}>{techWarning}</Typography>
          </Alert>
        )}
        {countryWarnings && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">Some selected countries are not available in the selected test suites:</Typography>
            {countryWarnings.map((warning, idx) => {
              const countryList = warning.countries.length > 2 ? warning.countries.join(", ") : warning.countries.join(" & ");
              return (
                <Box key={idx} sx={{ mt: 0.5 }}>
                  <Typography variant="body2">{countryList} are missing from <strong>{warning.testSuiteName}</strong></Typography>
                </Box>
              );
            })}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" component="span">Update test suites to include them?</Typography>
              <Button size="small" variant="outlined" onClick={handleUpdateTestSuite} sx={{ ml: 1 }}>Update</Button>
            </Box>
          </Alert>
        )}
      </Box>
    );
  };

  const renderAutomationFields = () => {
    const boolOptions = [{ id: "Yes", name: "Yes" }, { id: "No", name: "No" }];
    const automationStatusOptions = automationStatuses.map((s) => ({ id: s, name: s, disabled: s === "Done" }));

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2.5 }}>
          {/* Candidate for Automation */}
          {isEditing ? (
            <CustomSelect
              id="isAutomationNeeded"
              label="Candidate for Automation"
              value={formData.isAutomationNeeded === true ? "Yes" : "No"}
              onChange={(val) => handleInputChange("isAutomationNeeded", val === "Yes")}
              options={boolOptions}
            />
          ) : (
            <FieldDisplay label="Candidate for Automation">
              {formData.isAutomationNeeded ? "Yes" : "No"}
            </FieldDisplay>
          )}

          <Box>
            {formData.isAutomationNeeded === true && (
              isEditing ? (
                <CustomSelect
                  id="automationStatus"
                  label="Automation Status"
                  value={formData.automationStatus || "Todo"}
                  onChange={(val) => handleInputChange("automationStatus", val)}
                  options={automationStatusOptions}
                  disabled={!formData.isAutomationNeeded || formData.isAutomated === "Yes"}
                />
              ) : (
                <FieldDisplay label="Automation Status">{formData.automationStatus}</FieldDisplay>
              )
            )}
          </Box>
          <Box>
            {formData.isAutomationNeeded === true && (
              isEditing ? (
                <CustomSelect
                  id="isAutomated"
                  label="Is Automated?"
                  value={formData.isAutomated || "No"}
                  onChange={(val) => handleInputChange("isAutomated", val)}
                  options={boolOptions}
                />
              ) : (
                <FieldDisplay label="Is Automated?">{formData.isAutomated}</FieldDisplay>
              )
            )}
          </Box>
        </Box>

        {/* Not automated reason section */}
        {(formData.isAutomationNeeded === false || formData.isAutomated === "No") && (
          <>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Reason for not automating
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2.5 }}>
              {/* Category */}
              {isEditing ? (
                <CustomSelect
                  id="notAutomatedCategory"
                  label={
                    <>
                      Category
                      {formData.isAutomationNeeded === true && formData.isAutomated === "No" && formData.automationStatus !== "To Be Planned" && (
                        <Box component="span" sx={{ color: "error.main" }}> *</Box>
                      )}
                    </>
                  }
                  value={formData.notAutomatedCategory || ""}
                  onChange={(val) => handleNotAutomatedChange("category", val)}
                  options={[{ id: "", name: "None" }, ...toOptions(notAutomatedCategories)]}
                  error={stepErrors.notAutomatedCategory}
                />
              ) : (
                <FieldDisplay label="Category">
                  {notAutomatedCategories.find((c) => c.id === formData.notAutomatedCategory)?.name || "None"}
                </FieldDisplay>
              )}

              {/* Sub Category */}
              {isEditing ? (
                <CustomSelect
                  id="notAutomatedSubCategory"
                  label={
                    <>
                      Sub Category
                      {formData.isAutomationNeeded === true && formData.isAutomated === "No" && formData.automationStatus !== "To Be Planned" && (
                        <Box component="span" sx={{ color: "error.main" }}> *</Box>
                      )}
                    </>
                  }
                  value={formData.notAutomatedSubCategory || ""}
                  onChange={(val) => handleNotAutomatedChange("subcategory", val)}
                  options={[{ id: "", name: "None" }, ...toOptions(notAutomatedSubCategories)]}
                  disabled={!notAutomatedSubCategories.length || !formData.notAutomatedCategory}
                  error={stepErrors.notAutomatedSubCategory}
                />
              ) : (
                <FieldDisplay label="Sub Category">
                  {notAutomatedSubCategories.find((s) => s.id === formData.notAutomatedSubCategory)?.name || "None"}
                </FieldDisplay>
              )}
              <Box />
            </Box>

            {/* Reason */}
            {isEditing ? (
              <CustomInputField
                id="reasonForNotAutomated"
                label="Reason"
                value={formData.reasonForNotAutomated || ""}
                onChange={handleInputChange}
                placeholder="Enter reason"
                maxLength={255}
                multiline
                rows={2}
              />
            ) : (
              <FieldDisplay label="Reason">
                {formData.reasonForNotAutomated || formatPlaceholder()}
              </FieldDisplay>
            )}
          </>
        )}

        {/* Automated details */}
        {formData.isAutomationNeeded === true && formData.isAutomated === "Yes" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2.5 }}>
            {/* Automated By */}
            {isEditing ? (
              <CustomSelect
                id="automatedBy"
                label="Automated By"
                required
                value={projectUsers?.some((u) => u.id === formData.automatedBy) ? formData.automatedBy : ""}
                onChange={(val) => handleInputChange("automatedBy", val)}
                options={usersToOptions(projectUsers)}
                placeholder="Select automated by"
              />
            ) : (
              <FieldDisplay label="Automated By">{showUserDisplayName(formData?.automatedBy, projectUsers)}</FieldDisplay>
            )}

            {/* Automation Method */}
            {isEditing ? (
              <CustomInputField
                id="automationMethod"
                label="Automation Method"
                required
                value={formData.automationMethod || ""}
                onChange={handleInputChange}
                placeholder="Enter automation method"
                maxLength={100}
                error={stepErrors.automationMethod}
              />
            ) : (
              <FieldDisplay label="Automation Method">{formData.automationMethod || formatPlaceholder()}</FieldDisplay>
            )}

            <Box>
            {hasCountryConfig && (
              isEditing ? (() => {
                const availableCountry = formData?.country?.length > 0 ? formData.country : [];
                const formattedAuto = formatCountryOptions(availableCountry);
                const autoDisplayMap = {};
                formattedAuto.forEach((item) => { autoDisplayMap[item.value] = item.display; });
                const countryOpts = availableCountry?.map((g) => ({ id: g, name: g }));
                return (
                  <Box sx={{ display: "grid", gap: 0.5 }}>
                    <MultiSelectDropdown
                      label="Automated for Country"
                      options={countryOpts}
                      selectedValues={filterSelectedValues(formData?.automationCountry || [], availableCountry)}
                      onChange={(value) => {
                        setFormData((prev) => ({ ...prev, automationCountry: value }));
                        setIsFormEdited(true);
                      }}
                      placeholder="Select countries"
                      disabled={availableCountry.length === 0}
                      name="countries"
                      renderOption={(opt) => autoDisplayMap[opt.id] || opt.name}
                    />
                    {availableCountry.length === 0 && (
                      <Typography variant="caption" color="error.main">
                        Please select countries for test case first.
                      </Typography>
                    )}
                  </Box>
                );
              })() : (
                <FieldDisplay label="Automated for Country">
                  {formData?.automationCountry?.length > 0
                    ? filterSelectedValues(formData.automationCountry, projectConfig?.geographies).join(", ")
                    : "None"}
                </FieldDisplay>
              )
            )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };


  if (loading) return <SkeletonLoader />;

  if (isCreateMode) {
    const STEP_LABELS = ["Basic Info", "Classification", "Automation"];
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3.5 }}>
          <Stepper activeStep={createStep - 1} alternativeLabel sx={{ width: "100%", maxWidth: 480 }}>
            {STEP_LABELS.map((label, idx) => {
              const stepNum = idx + 1;
              const isCompleted = stepNum < createStep;
              return (
                <Step key={label} completed={isCompleted}>
                  <StepLabel
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontSize: "0.75rem",
                        fontWeight: createStep === stepNum ? 600 : 400,
                        color: createStep === stepNum
                          ? "primary.main"
                          : isCompleted
                            ? "success.main"
                            : "text.disabled",
                      },
                      "& .MuiStepIcon-root": {
                        color: isCompleted ? "success.main" : createStep === stepNum ? "primary.main" : "grey.300",
                      },
                      "& .MuiStepIcon-text": {
                        fill: "common.white",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        <Box sx={{ minHeight: 280 }}>
          {createStep === 1 && renderBasicFields()}
          {createStep === 2 && renderClassificationFields()}
          {createStep === 3 && (
            <>
              {renderAutomationFields()}
              {onAddSteps && (
                <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Button variant="outlined" size="small" startIcon={<FormatListBulletedIcon />} onClick={onAddSteps}>
                    {stepsCount > 0 ? `Test Steps (${stepsCount})` : "Add Test Steps"}
                  </Button>
                  {stepsCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {stepsCount} step{stepsCount !== 1 ? "s" : ""} added
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isEditing ? (
        <CustomInputField id="summary" label="Description" required value={formData.summary || ""} onChange={handleInputChange} placeholder="Enter description" maxLength={255} error={stepErrors.summary} />
      ) : (
        <FieldDisplay label="Description">
          {formData.summary}
        </FieldDisplay>
      )}

      {isEditing ? (
        <CustomInputField id="objective" label="Objective" value={formData.objective || ""} onChange={handleInputChange} placeholder="Enter objective" maxLength={500} multiline rows={3} />
      ) : (
        <FieldDisplay label="Objective">
          {formData.objective || formatPlaceholder("Add an objective")}
        </FieldDisplay>
      )}

      {isEditing ? (
        <CustomInputField id="preCondition" label="Pre-condition" value={formData.preCondition || ""} onChange={handleInputChange} placeholder="Enter pre-condition" maxLength={500} multiline rows={3} />
      ) : (
        <FieldDisplay label="Pre-condition">
          {formData.preCondition || formatPlaceholder("Add a pre-condition")}
        </FieldDisplay>
      )}

      <Typography variant="h6" sx={{ mt: 2, mb: 0.5, fontWeight: 600, fontSize: "0.9375rem" }}>
        Additional Information
      </Typography>
      {renderClassificationFields()}

      <Typography variant="h6" sx={{ mt: 3, mb: 0.5, fontWeight: 600, fontSize: "0.9375rem" }}>
        Automation Details
      </Typography>
      {renderAutomationFields()}
    </Box>
  );
};

export default CreateTestCaseForm;